import {
    DataGrid,
    DataGridHeader,
    DataGridBody,
    DataGridRow,
    DataGridCell,
    DataGridHeaderCell,
    createTableColumn,
    type TableColumnDefinition,
} from "@fluentui/react-components";
import { Edit, Trash, Search, Download } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";
import { useMemo, useState } from "react";
import { type ApplicationDetailsRow, type ApplicationDetailsTableProps } from "../types/application";
import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ApplicationDetailsTable({ rows, onEdit, onDelete, onDownload }: ApplicationDetailsTableProps) {
    const { t } = useTranslation();
    const [deleteConfirm, setDeleteConfirm] = useState<{
        isOpen: boolean;
        application: ApplicationDetailsRow | null;
    }>({
        isOpen: false,
        application: null,
    });

    const [sortColumn, setSortColumn] = useState<keyof ApplicationDetailsRow | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [page, setPage] = useState(1);
    const [openFeedback, setOpenFeedback] = useState<string | null>(null);

    const pageSize = 6;

    const toggleSort = (column: keyof ApplicationDetailsRow) => {
        setSortColumn(column);
        setSortDirection(prev =>
            prev === "asc" && sortColumn === column ? "desc" : "asc"
        );
    };

    const SortIcon = (column: keyof ApplicationDetailsRow) =>
        sortColumn === column ? (sortDirection === "asc" ? "▲" : "▼") : null;

    const filteredRows = useMemo(() => {
        return rows.filter(row =>
            Object.values(row).some(val =>
                String(val).toLowerCase().includes(globalFilter.toLowerCase())
            )
        );
    }, [rows, globalFilter]);

    const sortedRows = useMemo(() => {
        if (!sortColumn || !sortDirection) return filteredRows;

        return [...filteredRows].sort((a, b) => {
            const A = String(a[sortColumn] ?? "");
            const B = String(b[sortColumn] ?? "");
            return sortDirection === "asc"
                ? A.localeCompare(B)
                : B.localeCompare(A);
        });
    }, [filteredRows, sortColumn, sortDirection]);

    const paginatedRows = sortedRows.slice(
        (page - 1) * pageSize,
        page * pageSize
    );
    const columns: TableColumnDefinition<ApplicationDetailsRow>[] = [
        createTableColumn({
            columnId: "applicant_name",
            renderHeaderCell: () => (
                <div
                    className="flex gap-1 cursor-pointer"
                    onClick={() => toggleSort("applicant_name")}
                >
                    {t("applicationDetails.applicant")} {SortIcon("applicant_name")}
                </div>
            ),
            renderCell: item => (
                <div className="flex items-center justify-between gap-2 w-full">
                    <span className="font-semibold text-gray-700 truncate">
                        {item.applicant_name ?? item.file_name ?? t("applicationDetails.unknown")}
                    </span>

                    <button
                        type="button"
                        className="p-1 bg-green-50 rounded hover:bg-green-100 cursor-pointer transition-colors"
                        onClick={() => onDownload(item)}
                        title={t("applicationDetails.downloadDocument")}
                    >
                        <Download size={16} className="text-green-600" />
                    </button>
                </div>
            ),
        }),
        createTableColumn({
            columnId: "fitness_score",
            renderHeaderCell: () => (
                <div
                    className="flex flex-col items-center text-center cursor-pointer"
                    onClick={() => toggleSort("fitness_score")}
                >
                    {t("applicationDetails.roleAlignmentScore")} {SortIcon("fitness_score")}
                </div>
            ),
            renderCell: item => (
                <div className="w-full flex justify-center items-center font-medium text-gray-700">
                    {item.fitness_score !== null && item.fitness_score !== undefined
                        ? Number(item.fitness_score).toFixed(2)
                        : "-"}
                </div>
            ),
        }),
        createTableColumn({
            columnId: "fakeness_score",
            renderHeaderCell: () => (
                <div
                    className="flex gap-1 cursor-pointer justify-center"
                    onClick={() => toggleSort("fakeness_score")}
                >
                    {t("applicationDetails.authenticityQuotient")} {SortIcon("fakeness_score")}
                </div>
            ),
            renderCell: item => (
                <div className="w-full flex justify-center items-center font-medium text-gray-700">
                    {item.fakeness_score !== null && item.fakeness_score !== undefined
                        ? Number(item.fakeness_score).toFixed(2)
                        : "-"}
                </div>
            ),
        }),
        createTableColumn({
            columnId: "status",
            renderHeaderCell: () => (
                <div
                    className="w-full flex justify-center cursor-pointer"
                    onClick={() => toggleSort("status")}
                >
                    {t("applicationDetails.status")} {SortIcon("status")}
                </div>
            ),
            renderCell: item => (
                <div className="w-full flex justify-center">
                    <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-semibold">
                        {item.status}
                    </span>
                </div>
            ),
        }),
        createTableColumn({
            columnId: "feedback",
            renderHeaderCell: () => (
                <div className="w-full flex justify-center">
                    {t("applicationDetails.feedback")}
                </div>
            ),
            renderCell: item => (
                <div className="w-full flex justify-center">
                    <button
                        className="p-1 bg-blue-50 rounded hover:bg-blue-100"
                        title={t("applicationDetails.viewFeedback")}
                        onClick={() =>
                            setOpenFeedback(
                                item.feedback || t("applicationDetails.noDetailedAnalysis")
                            )
                        }
                    >
                        <FileText size={16} className="text-blue-600" />
                    </button>
                </div>
            ),
        }),
        createTableColumn({
            columnId: "actions",
            renderHeaderCell: () => (
                <div className="w-full flex justify-center">
                    {t("applicationDetails.actions")}
                </div>
            ),
            renderCell: item => (
                <div className="w-full flex justify-center gap-1">
                    <button
                        type="button"
                        className="p-1 bg-gray-100 rounded hover:bg-gray-200 cursor-pointer transition-colors"
                        onClick={() => onEdit(item)}
                        title={t("applicationDetails.editApplication")}
                    >
                        <Edit size={16} className="text-gray-700" />
                    </button>

                    <button
                        type="button"
                        className="p-1 bg-red-50 rounded hover:bg-red-100 cursor-pointer transition-colors"
                        onClick={() => setDeleteConfirm({ isOpen: true, application: item })}
                        title={t("applicationDetails.deleteApplication")}
                    >
                        <Trash size={16} className="text-red-600" />
                    </button>
                </div>
            ),
        }),
    ];

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 overflow-hidden">
            <div className="flex items-start justify-between gap-6 mb-4">
                {/* Left Side */}
                <div className="flex flex-col">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                        {t("applicationDetails.title")}
                    </h3>

                    <div className="max-w-xs w-[440px] relative">
                        <Search className="absolute left-2 top-2.5 text-gray-400" size={16} />

                        <input
                            className="pl-8 border rounded w-full py-2 outline-none text-sm"
                            placeholder={t("applicationDetails.searchPlaceholder")}
                            value={globalFilter}
                            onChange={(e) => {
                                setPage(1);
                                setGlobalFilter(e.target.value);
                            }}
                        />
                    </div>
                </div>

                {/* Right Side RAS/AQ */}
                <div className="flex-1 max-w-5xl pt-1">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 w-full">
                        <div className="text-sm text-gray-700 leading-relaxed">
                            <span className="font-semibold">RAS :</span>
                            {" "} indicates the suitability of the profile
                            with respect to the Job description
                        </div>

                        <div className="text-sm text-gray-700 leading-relaxed mt-2">
                            <span className="font-semibold">AQ :</span>
                            {" "} indicates the probability score
                            of profile creation using AI
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full overflow-x-auto">
                <DataGrid
                    items={paginatedRows}
                    columns={columns}
                    resizableColumns
                    getRowId={(item) => String(item.application_id)}
                    style={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}
                    resizableColumnsOptions={{
                        autoFitColumns: false,
                    }}
                    columnSizingOptions={{
                        applicant_name: {
                            minWidth: 200,
                            idealWidth: 250,
                            defaultWidth: 220,
                        },
                        download: {
                            minWidth: 80,
                            idealWidth: 80,
                            defaultWidth: 80,
                        },
                        fitness_score: {
                            minWidth: 170,
                            idealWidth: 250,
                            defaultWidth: 210,
                        },
                        fakeness_score: {
                            minWidth: 170,
                            idealWidth: 240,
                            defaultWidth: 200,
                        },
                        status: {
                            minWidth: 120,
                            idealWidth: 150,
                            defaultWidth: 140,
                        },
                        actions: {
                            minWidth: 70,
                            idealWidth: 70,
                            defaultWidth: 70,
                        },
                    }}
                >
                    <DataGridHeader>
                        <DataGridRow>
                            {({ renderHeaderCell }) => (
                                <DataGridHeaderCell className="bg-gray-100 font-semibold sticky top-0 z-10 border-b border-r border-gray-300">
                                    {renderHeaderCell()}
                                </DataGridHeaderCell>
                            )}
                        </DataGridRow>
                    </DataGridHeader>

                    <DataGridBody>
                        {({ item }) => (
                            <DataGridRow>
                                {({ renderCell }) => (
                                    <DataGridCell className="border-b border-r border-gray-300">
                                        {renderCell(item)}
                                    </DataGridCell>
                                )}
                            </DataGridRow>
                        )}
                    </DataGridBody>
                </DataGrid>
            </div>
            {/* Pagination */}
            <div className="flex justify-end gap-3 mt-3">
                <button
                    className="px-3 py-1 border rounded text-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    {t("applicationDetails.prev")}
                </button>

                <span className="text-sm">
                    Page {page} of {Math.ceil(sortedRows.length / pageSize)}
                </span>

                <button
                    className="px-3 py-1 border rounded text-sm"
                    disabled={page === Math.ceil(sortedRows.length / pageSize)}
                    onClick={() => setPage(page + 1)}
                >
                    {t("applicationDetails.next")}
                </button>
            </div>

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                title={t("applicationDetails.deleteApplication")}
                message={t("applicationDetails.deleteConfirmMessage", {
                    name: deleteConfirm.application?.applicant_name ?? deleteConfirm.application?.file_name ?? t("applicationDetails.unknown"),
                })}
                confirmText={t("applicationDetails.delete")}
                cancelText={t("applicationDetails.cancel")}
                onConfirm={() => {
                    if (deleteConfirm.application) {
                        onDelete(deleteConfirm.application.application_id);
                    }
                    setDeleteConfirm({ isOpen: false, application: null });
                }}
                onCancel={() => setDeleteConfirm({ isOpen: false, application: null })}
            />

            {openFeedback && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white w-full max-w-[50rem] p-6 rounded-xl shadow-lg relative">
                        <button
                            onClick={() => setOpenFeedback(null)}
                            className="absolute top-2 right-2 text-gray-500"
                            aria-label={t("applicationDetails.close")}
                        >
                            ✕
                        </button>

                        <h2 className="text-lg font-bold mb-4">{t("applicationDetails.feedback")}</h2>

                        <ul className="text-gray-700 space-y-2 list-disc pl-5 max-h-[400px] overflow-y-auto">
                            {openFeedback
                                .split(/(?<=[.!?])\s+/)
                                .filter(Boolean)
                                .map((point, i) => (
                                    <li key={i}>{point}</li>
                                ))}
                        </ul>
                    </div>
                </div>
            )}
        </div >
    );
}
